import { useRef, useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import cookie from 'cookie'

import AdButlerPlacement from '../AdButlerPlacement'
import { setCloseBannerVisibility } from 'src/redux/actions'

import styles from './styles.module.scss'

// Cookie key for tracking closed banner
const CLOSE_BANNER_COOKIE = 'closeBannerClosed'
const COOKIE_EXPIRY_HOURS = 24

const CloseBanner = () => {
  const dispatch = useDispatch()
  const router = useRouter()
  const isShare = router.asPath.includes('getScreen')
  const wrapperRef = useRef()
  const [isAboutToClose, setIsAboutToClose] = useState(false)
  const [showTimeout, setShowTimeout] = useState(null)
  const [isAdReady, setIsAdReady] = useState(false) // Add loading state for AdButler

  // Check if banner was recently closed
  const isBannerRecentlyClosed = () => {
    if (typeof document === 'undefined') return false // SSR check

    const cookies = cookie.parse(document.cookie)
    return !!cookies[CLOSE_BANNER_COOKIE]
  }

  // Set cookie when banner is closed
  const setBannerClosedCookie = () => {
    if (typeof document === 'undefined') return // SSR check

    const expiryDate = new Date()
    expiryDate.setTime(
      expiryDate.getTime() + COOKIE_EXPIRY_HOURS * 60 * 60 * 1000
    ) // 1 hour

    document.cookie = cookie.serialize(CLOSE_BANNER_COOKIE, 'true', {
      expires: expiryDate,
      path: '/',
      sameSite: 'strict',
    })
  }

  const onEmpty = () => {
    // This will be called when AdButler content is empty or closed
    dispatch(setCloseBannerVisibility(false))
  }

  const handleClose = useCallback(() => {
    setIsAboutToClose(false)
    setIsAdReady(false)
    // Set cookie to remember user closed the banner
    setBannerClosedCookie()
    // Use the new action for close banner
    dispatch(setCloseBannerVisibility(false))
  }, [dispatch])

  // Add event listener for AdButler close button
  useEffect(() => {
    const handleAdButlerClose = () => {
      handleClose()
    }

    window.addEventListener('closeBannerOverlay', handleAdButlerClose)

    // Make the callback available globally for AdButler
    window.closeBannerCallback = () => {
      setIsAboutToClose(false)
      setIsAdReady(false)
      // Set cookie to remember user closed the banner
      setBannerClosedCookie()
      // Use the new action for close banner
      dispatch(setCloseBannerVisibility(false))
    }

    return () => {
      window.removeEventListener('closeBannerOverlay', handleAdButlerClose)
      delete window.closeBannerCallback
    }
  }, [dispatch, handleClose])

  // Add delay before showing AdButler to prevent flash
  useEffect(() => {
    if (isAboutToClose) {
      const timer = setTimeout(() => {
        setIsAdReady(true)
      }, 300) // 300ms delay to prevent flash

      return () => clearTimeout(timer)
    } else {
      setIsAdReady(false)
    }
  }, [isAboutToClose])

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Show the banner when user is about to leave
      setIsAboutToClose(true)

      // Optional: Show a confirmation dialog
      // e.preventDefault()
      // e.returnValue = ''
    }

    const handleVisibilityChange = () => {
      // Show banner when page becomes hidden (user switching tabs)
      if (document.hidden) {
        setIsAboutToClose(true)
      }
    }

    const handleBlur = () => {
      // Show banner when window loses focus (user clicking on another tab or window)
      const timeout = setTimeout(() => {
        setIsAboutToClose(true)
      }, 100)
      setShowTimeout(timeout)
    }

    const handleFocus = () => {
      // Clear timeout if window regains focus
      if (showTimeout) {
        clearTimeout(showTimeout)
        setShowTimeout(null)
      }
    }

    const handleMouseLeave = e => {
      // Show banner when mouse leaves the window (user hovering over close button)
      // Check if mouse is in the top area where close button typically is
      if (e.clientY <= 0 || e.clientX <= 0 || e.clientX >= window.innerWidth) {
        // Clear any existing timeout
        if (showTimeout) {
          clearTimeout(showTimeout)
        }

        // Set a timeout to show the banner after a short delay
        const timeout = setTimeout(() => {
          setIsAboutToClose(true)
        }, 200) // 200ms delay to avoid showing on accidental mouse movements

        setShowTimeout(timeout)
      }
    }

    const handleMouseEnter = () => {
      // Clear timeout if mouse re-enters the window
      if (showTimeout) {
        clearTimeout(showTimeout)
        setShowTimeout(null)
      }
    }

    const handleKeyDown = e => {
      // Show banner when user presses Alt+F4 or Cmd+W (common close shortcuts)
      if ((e.altKey && e.key === 'F4') || (e.metaKey && e.key === 'w')) {
        setIsAboutToClose(true)
      }
    }

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('mouseleave', handleMouseLeave)
    document.addEventListener('mouseenter', handleMouseEnter)
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('mouseleave', handleMouseLeave)
      document.removeEventListener('mouseenter', handleMouseEnter)
      document.removeEventListener('keydown', handleKeyDown)

      if (showTimeout) {
        clearTimeout(showTimeout)
      }
    }
  }, [showTimeout])

  // Only show the banner when user is about to close, NOT on initial page load
  // Remove the 'shown' condition to prevent showing on page load
  if (isShare || !isAboutToClose || isBannerRecentlyClosed()) {
    return null
  }

  return (
    <div className={styles.closeBannerOverlay}>
      <div className={styles.closeBanner} ref={wrapperRef}>
        {isAdReady && (
          <AdButlerPlacement
            place='962048'
            className={styles.placement}
            onEmpty={onEmpty}
            adjustHeight={true}
            forceProxy={true}
          />
        )}
      </div>
    </div>
  )
}

export default CloseBanner
